import { useEffect, useState } from "react";
import axios from "axios";
import { Sun, Moon, CloudSun, CloudMoon, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, Thermometer, Loader2, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WMO-Wettercode -> Icon + Bezeichnung (DE/EN)
const codeInfo = (code, isDay) => {
  const c = Number(code);
  if (c === 0) return { Icon: isDay ? Sun : Moon, de: "Klar", en: "Clear" };
  if (c === 1 || c === 2) return { Icon: isDay ? CloudSun : CloudMoon, de: "Teils bewölkt", en: "Partly cloudy" };
  if (c === 3) return { Icon: Cloud, de: "Bedeckt", en: "Overcast" };
  if (c === 45 || c === 48) return { Icon: CloudFog, de: "Nebel", en: "Fog" };
  if (c >= 51 && c <= 57) return { Icon: CloudDrizzle, de: "Nieselregen", en: "Drizzle" };
  if (c >= 61 && c <= 67) return { Icon: CloudRain, de: "Regen", en: "Rain" };
  if (c >= 71 && c <= 77) return { Icon: CloudSnow, de: "Schnee", en: "Snow" };
  if (c >= 80 && c <= 82) return { Icon: CloudRain, de: "Regenschauer", en: "Rain showers" };
  if (c === 85 || c === 86) return { Icon: CloudSnow, de: "Schneeschauer", en: "Snow showers" };
  if (c >= 95) return { Icon: CloudLightning, de: "Gewitter", en: "Thunderstorm" };
  return { Icon: Cloud, de: "Bewölkt", en: "Cloudy" };
};

const WeatherWidget = () => {
  const { lang } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const [w, setW] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let on = true;
    axios.get(`${API}/weather`).then((r) => on && setW(r.data)).catch(() => on && setErr(true));
    return () => { on = false; };
  }, []);

  const info = w ? codeInfo(w.weather_code, w.is_day) : null;
  const Icon = info?.Icon || Cloud;

  return (
    <div
      data-testid="weather-widget"
      className="relative overflow-hidden bg-[#0F0F0F] border border-[#ff0033]/60 hover:border-[#ff0033] transition-colors p-6 sm:p-8 flex flex-col h-full sm:min-h-[320px] md:min-h-[380px]"
    >
      <div
        className="absolute -right-16 -top-16 h-56 w-56 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,0,51,0.35) 0%, rgba(255,0,51,0) 70%)", filter: "blur(50px)" }}
      />
      <div className="relative z-10 flex flex-col h-full">
        <span className="font-mono-lab text-xs text-neutral-600">// {t("WETTER", "WEATHER")}</span>
        <div className="flex items-center gap-2 mt-3 text-neutral-400">
          <MapPin size={14} className="text-[#ff0033]" />
          <span className="text-xs font-mono-lab uppercase tracking-widest">Kirchheim-Wendlingen</span>
        </div>

        {!w ? (
          <div className="flex-1 flex items-center justify-center">
            {err ? (
              <p className="text-neutral-500 text-sm" data-testid="weather-error">{t("Wetterdaten momentan nicht verfügbar.", "Weather currently unavailable.")}</p>
            ) : (
              <Loader2 className="animate-spin text-[#ff0033]" size={28} data-testid="weather-loading" />
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mt-6">
              <Icon size={64} className="text-[#ff0033] shrink-0" strokeWidth={1.4} data-testid="weather-icon" />
              <div>
                <div className="font-heading text-5xl md:text-6xl text-white leading-none" data-testid="weather-temp">{w.temperature}°</div>
                <div className="text-neutral-300 text-sm mt-1">{t(info.de, info.en)}</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-auto pt-6">
              {[
                [Thermometer, t("Gefühlt", "Feels"), `${w.apparent_temperature}°`],
                [Wind, t("Wind", "Wind"), `${w.wind_speed} km/h`],
                [Droplets, t("Feuchte", "Humidity"), `${w.humidity}%`],
              ].map(([I, label, val]) => (
                <div key={label} className="border border-white/10 p-2.5">
                  <I size={15} className="text-neutral-500" />
                  <div className="text-white text-sm mt-1.5 font-heading">{val}</div>
                  <div className="text-neutral-500 text-[10px] font-mono-lab uppercase tracking-wider">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 text-[11px] font-mono-lab uppercase tracking-widest text-neutral-500">
              <span>{t("Heute", "Today")}</span>
              <span><span className="text-white">{w.temp_max}°</span> / {w.temp_min}°</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WeatherWidget;
